import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Routes, Route, useLocation } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ThemeToggle from '../../components/ui/ThemeToggle';
import { professionalClientAuthService } from '../../services/professionalClientAuthService';
import { professionalClientNotificationService } from '../../services/professionalClientNotificationService';
import VueEnsemble from './pages/VueEnsemble';
import MesExpeditions from './pages/MesExpeditions';
import MesDocuments from './pages/MesDocuments';
import AnalyseCouts from './pages/AnalyseCouts';
import Commander from './pages/Commander';
import MonCompte from './pages/MonCompte';

const ProfessionalClientDashboardV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [professionalClient, setProfessionalClient] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const notifRef = useRef(null);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef?.current && !notifRef?.current?.contains(e?.target)) {
        setNotifPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkAuth = async () => {
    try {
      setLoading(true);
      const client = await professionalClientAuthService?.getCurrentProfessionalClient();
      
      if (!client) {
        navigate('/professional-client/login');
        return;
      }

      setProfessionalClient(client);
      fetchUnreadCount();
    } catch (err) {
      console.error('Error checking auth:', err);
      navigate('/professional-client/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const count = await professionalClientNotificationService?.getUnreadCount();
      setUnreadNotifications(count || 0);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  };

  const fetchNotifications = async () => {
    try {
      setNotifLoading(true);
      const result = await professionalClientNotificationService?.getNotifications({ page: 1, limit: 10 });
      setNotifications(result?.notifications || []);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    } finally {
      setNotifLoading(false);
    }
  };

  const handleBellClick = () => {
    const newOpen = !notifPanelOpen;
    setNotifPanelOpen(newOpen);
    if (newOpen) {
      fetchNotifications();
    }
  };

  const handleMarkAsRead = async (notifId) => {
    try {
      await professionalClientNotificationService?.markAsRead(notifId);
      setNotifications(prev => prev?.map(n => n?.id === notifId ? { ...n, isRead: true } : n));
      setUnreadNotifications(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadIds = notifications?.filter(n => !n?.isRead)?.map(n => n?.id);
      if (unreadIds?.length === 0) return;
      await professionalClientNotificationService?.markMultipleAsRead(unreadIds);
      setNotifications(prev => prev?.map(n => ({ ...n, isRead: true })));
      setUnreadNotifications(0);
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const getNotifIcon = (type) => {
    switch (type) {
      case 'shipment_status': return 'Package';
      case 'invoice_ready': return 'FileText';
      case 'certificate_ready': return 'Award';
      case 'container_departure': return 'Ship';
      default: return 'Bell';
    }
  };

  const formatNotifDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'À l\'instant';
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
    return date?.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  const handleSignOut = async () => {
    try {
      await professionalClientAuthService?.signOut();
      toast?.success('Déconnexion réussie');
      navigate('/professional-client-login');
    } catch (error) {
      console.error('Sign out error:', error);
      toast?.error('Erreur lors de la déconnexion');
    }
  };

  const menuItems = [
    { path: '/professional-dashboard/vue-ensemble', label: 'Vue d\'ensemble', icon: 'Home' },
    { path: '/professional-dashboard/mes-expeditions', label: 'Mes expéditions', icon: 'Package' },
    { path: '/professional-dashboard/mes-documents', label: 'Mes documents', icon: 'FileText' },
    { path: '/professional-dashboard/analyse-couts', label: 'Analyse des coûts', icon: 'BarChart3' },
    { path: '/professional-dashboard/commander', label: 'Commander', icon: 'ShoppingCart' },
    { path: '/professional-dashboard/mon-compte', label: 'Mon compte', icon: 'Settings' }
  ];

  const isActive = (path) => location?.pathname === path;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Chargement de votre espace pro...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
      <Toaster position="top-right" />

      {/* Top Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
          >
            <Icon name="Menu" size={24} className="text-gray-700 dark:text-gray-300" />
          </button>

          {/* Logo */}
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">ADT</span>
            </div>
            <span className="hidden sm:block font-bold text-gray-900 dark:text-white">ADT Express Pro</span>
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-3">
            {/* Theme Toggle */}
            <ThemeToggle />
            
            {/* Company Name */}
            <div className="hidden md:block text-right mr-4">
              <p className="text-sm font-medium text-gray-900 dark:text-white">{professionalClient?.companyName}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{professionalClient?.contactName}</p>
            </div>

            {/* Notifications */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={handleBellClick}
                className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                <Icon name="Bell" size={20} className="text-gray-700 dark:text-gray-300" />
                {unreadNotifications > 0 && (
                  <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
              </button>

              {/* Notification Panel */}
              {notifPanelOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 z-50 overflow-hidden">
                  {/* Panel Header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
                    <div className="flex items-center space-x-2">
                      <Icon name="Bell" size={16} className="text-gray-700 dark:text-gray-300" />
                      <span className="font-semibold text-gray-900 dark:text-white text-sm">Notifications</span>
                      {unreadNotifications > 0 && (
                        <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                          {unreadNotifications}
                        </span>
                      )}
                    </div>
                    {unreadNotifications > 0 && (
                      <button
                        onClick={handleMarkAllAsRead}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
                      >
                        Tout marquer lu
                      </button>
                    )}
                  </div>

                  {/* Notifications List */}
                  <div className="max-h-96 overflow-y-auto">
                    {notifLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                      </div>
                    ) : notifications?.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-10 text-center px-4">
                        <div className="w-12 h-12 bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center mb-3">
                          <Icon name="BellOff" size={20} className="text-gray-400" />
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Aucune notification</p>
                      </div>
                    ) : (
                      notifications?.map((notif) => (
                        <div
                          key={notif?.id}
                          onClick={() => !notif?.isRead && handleMarkAsRead(notif?.id)}
                          className={`flex items-start space-x-3 px-4 py-3 border-b border-gray-100 dark:border-slate-700 last:border-0 transition-colors ${
                            !notif?.isRead
                              ? 'bg-blue-50 dark:bg-slate-700/50 cursor-pointer hover:bg-blue-100 dark:hover:bg-slate-700' :'bg-white dark:bg-slate-800'
                          }`}
                        >
                          <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5 ${
                            !notif?.isRead ? 'bg-blue-100 dark:bg-blue-900/40' : 'bg-gray-100 dark:bg-slate-700'
                          }`}>
                            <Icon
                              name={getNotifIcon(notif?.notificationType)}
                              size={14}
                              className={!notif?.isRead ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm leading-snug ${
                              !notif?.isRead ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-300'
                            }`}>
                              {notif?.subject || notif?.message || 'Nouvelle notification'}
                            </p>
                            {notif?.message && notif?.subject && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{notif?.message}</p>
                            )}
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{formatNotifDate(notif?.createdAt)}</p>
                          </div>
                          {!notif?.isRead && (
                            <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Contact Advisor Button */}
            <Button
              onClick={() => window.open('https://wa.me/33123456789', '_blank')}
              className="hidden sm:flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white"
              size="sm"
            >
              <Icon name="Phone" size={16} />
              <span>Contacter mon conseiller pro</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed lg:sticky top-[57px] left-0 h-[calc(100vh-57px)] bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700 transition-transform duration-300 z-30 ${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          } w-64`}
        >
          <nav className="p-4 space-y-2">
            {menuItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => {
                  navigate(item?.path);
                  setIsSidebarOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive(item?.path)
                    ? 'bg-gradient-to-r from-green-500 to-blue-600 text-white shadow-md'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                }`}
              >
                <Icon name={item?.icon} size={20} />
                <span className="font-medium">{item?.label}</span>
              </button>
            ))}

            {/* Sign Out Button */}
            <button
              onClick={handleSignOut}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors mt-4"
            >
              <Icon name="LogOut" size={20} />
              <span className="font-medium">Déconnexion</span>
            </button>
          </nav>
        </aside>

        {/* Mobile Overlay */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-y-auto">
          <Routes>
            <Route path="vue-ensemble" element={<VueEnsemble professionalClient={professionalClient} />} />
            <Route path="mes-expeditions" element={<MesExpeditions professionalClient={professionalClient} />} />
            <Route path="mes-documents" element={<MesDocuments professionalClient={professionalClient} />} />
            <Route path="analyse-couts" element={<AnalyseCouts professionalClient={professionalClient} />} />
            <Route path="commander" element={<Commander professionalClient={professionalClient} />} />
            <Route path="mon-compte" element={<MonCompte professionalClient={professionalClient} />} />
            <Route path="*" element={<VueEnsemble professionalClient={professionalClient} />} />
          </Routes>
        </main>
      </div>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/33123456789"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 bg-green-500 hover:bg-green-600 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110 z-50"
      >
        <Icon name="MessageCircle" size={28} className="text-white" />
      </a>
    </div>
  );
};

export default ProfessionalClientDashboardV2;